import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker, Tag } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { Marquee } from "@/components/Marquee";

const SOMEONE = [
  "Someone who can yarn.",
  "Someone who can listen.",
  "Someone who can pick them up when the day has fallen apart.",
  "Someone who can walk beside them.",
  "Someone who understands that school, family, culture, placement, identity, relationships and wellbeing do not exist separately.",
  "Someone who sees knowledge and strength before problems and behaviour.",
];

const GAP = [
  ["School", "affects home."],
  ["Home", "affects placement."],
  ["Placement", "affects belonging."],
  ["Belonging", "affects identity."],
  ["Family contact", "affects emotions."],
  ["Emotions", "affect school."],
  ["Carer employment stress", "can affect the whole household."],
];

const About = () => (
  <div data-testid="about-page">
    <PageHero
      kicker="About Ngawilyi"
      title={"Not another worker outside the family.\nAn Aunty walking alongside them."}
      lede="Ngawilyi Narrative is an emerging Aboriginal-owned narrative therapy, mentoring, movement and community organisation supporting Aboriginal children and young people, parents, kinship carers, foster carers and families across the overlapping areas of life."
    />

    <section className="px-6 py-20 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Kicker>Our philosophy</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Every family needs <span className="italic text-terracotta">an extra Aunty for a yarn.</span>
          </h2>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth">
            Sometimes children and families do not need another referral, another assessment, another appointment
            or another person asking them to tell their story again. Sometimes they need someone consistent beside
            them. Ngawilyi Narrative provides an Aunty's all-round approach — a relational bridge across the spaces
            services leave between them.
          </p>
        </Reveal>
        <div className="space-y-3">
          {SOMEONE.map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="rounded-3xl bg-creamdark border border-ochre/25 px-7 py-5 font-display text-xl sm:text-2xl text-earth">
                {s}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <Marquee items={["One relationship.", "Multiple life domains.", "Less system overwhelm."]} dark={false} />

    <section className="px-6 py-20 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
        <Reveal>
          <Kicker>The gap we're responding to</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            People's lives don't fit neatly into <span className="italic text-terracotta">service categories.</span>
          </h2>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth">
            Children and families experiencing multiple overlapping concerns can quickly become surrounded by
            services — a different program, referral or worker for each concern. Ngawilyi Narrative was imagined as
            another way of working: someone able to yarn, mentor, move, advocate, connect, create and respond
            according to what is happening in the person's life.
          </p>
          <p className="mt-5 text-base lg:text-lg leading-relaxed text-earth">
            Ngawilyi does not aim to replace specialist, statutory, clinical or cultural services. It aims to work
            alongside them — and bridge the spaces between them.
          </p>
        </Reveal>
        <div className="rounded-[40px] bg-charcoal text-cream p-10 sm:p-12 grain relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-terracotta/30 organic-blob blur-2xl" />
          <div className="relative space-y-4">
            {GAP.map(([a, b], i) => (
              <Reveal key={i} delay={i * 0.06}>
                <p className="font-display text-2xl sm:text-3xl font-light">
                  {a} <span className="italic text-ochre">{b}</span>
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="px-6 py-20 sm:py-28 bg-blush/40 grain relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-ochre/25 organic-blob blur-3xl" />
      <div className="relative max-w-5xl mx-auto">
        <Reveal>
          <Kicker>The name</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Ngawilyi means <span className="italic text-terracotta">"Aunty"</span> in Arabana language.
          </h2>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth max-w-3xl">
            Because our name carries that meaning, some people come to us searching for "Aunties Narrative" —
            if that's how you found us, you're in the right place. Ngawilyi Narrative is our name; Aunties
            Narrative is simply another way people recognise and find us.
          </p>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth max-w-3xl">
            The use of Arabana language and authorisation for the program and name has been provided through
            Arabana Elders and the Arabana Aboriginal Corporation. This cultural authority is held with deep
            respect. Ngawilyi Narrative does not claim cultural authority for other Aboriginal Nations — when
            working alongside children and families from other Nations, we support connection with their own
            family, community, Elders, Country and cultural knowledge holders.
          </p>
          <div className="mt-8"><Tag>Authorised through Arabana Elders & the Arabana Aboriginal Corporation</Tag></div>
        </Reveal>
      </div>
    </section>

    <section className="px-6 py-20 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Kicker>The aim</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            The goal is not dependence on Ngawilyi.
          </h2>
        </Reveal>
        <div className="space-y-4">
          {[
            ["Community", "around the child."],
            ["Knowledge", "within the child."],
            ["Strong relationships", "beside the child."],
          ].map(([a, b], i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="rounded-[32px] bg-creamdark border border-ochre/25 px-8 py-7">
                <p className="font-display text-3xl font-light text-charcoal">{a} <span className="italic text-terracotta">{b}</span></p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <p className="pt-4 text-base leading-relaxed text-earth">
              Ngawilyi Narrative is an emerging organisation. Our programs are proposed models being shaped through
              community consultation — we are seeking partners, pilot funders and community voices rather than
              claiming finished outcomes.
            </p>
            <Link data-testid="about-consult-btn" to="/consultation" className="group mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-terracotta">
              Help Shape Ngawilyi <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    {/* ABOUT OUR AUNTIES */}
    <section className="px-6 py-20 sm:py-28 bg-creamdark grain relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-terracotta/12 organic-blob blur-3xl" />
      <div className="relative max-w-7xl mx-auto">
        <Reveal>
          <Kicker>About Our Aunties</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-charcoal max-w-4xl">
            An Aunties <span className="italic text-terracotta">All-Round Approach.</span>
          </h2>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth max-w-3xl">
            Ngawilyi Narrative is an Aboriginal-owned narrative therapy, mentoring, fitness and community
            organisation. Our Aunties are narrative therapists who bridge therapy, culture, community, movement
            and everyday life — creating flexible spaces where children, young people and families can receive
            therapeutic support without losing the importance of relationship, culture and connection.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 rounded-[40px] bg-cream border border-ochre/30 p-10 sm:p-12">
            <p className="font-display font-light text-3xl sm:text-4xl leading-snug text-charcoal">
              Therapy does not always need to happen <span className="italic text-terracotta">across a desk.</span>
            </p>
            <p className="mt-5 text-earth/85 leading-relaxed max-w-2xl">
              Narrative conversations can happen while —
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {["yarning", "walking", "creating", "exercising", "cooking", "spending time in community", "working alongside a child and family through a difficult day"].map((t) => (
                <span key={t} className="rounded-full bg-creamdark border border-ochre/40 px-5 py-2.5 text-sm font-semibold text-earth">{t}</span>
              ))}
            </div>
            <p className="mt-8 font-display italic text-2xl text-terracotta">Healing through story. Strength through connection.</p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* FOUNDER PROFILE */}
    <section className="px-6 py-20 sm:py-28 bg-cream" data-testid="founder-profile">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-12">
          <Kicker>Founder, CEO & Lead Narrative Therapist</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            The Aunty who built <span className="italic text-terracotta">the bridge.</span>
          </h2>
        </Reveal>
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-4">
            <div className="sticky top-32">
              <div className="organic-blob overflow-hidden bg-gradient-to-br from-terracotta via-sunset to-ochre aspect-[4/5] grain relative shadow-[0_30px_70px_rgba(192,87,70,0.3)]">
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="font-display italic text-cream/90 text-2xl text-center px-8">Founder portrait<br />coming soon</p>
                </div>
              </div>
              <p className="mt-6 font-display text-3xl text-charcoal">Angel</p>
              <p className="mt-1 text-xs uppercase tracking-[0.25em] font-bold text-terracotta">Founder, CEO & Lead Narrative Therapist</p>
              <p className="mt-2 text-sm text-earth/75 leading-relaxed">
                Aboriginal-owned and led. Professional narrative therapist, community practitioner, mother, kinship carer, foster carer and Aunty.
              </p>
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <h3 className="font-display text-2xl sm:text-3xl text-charcoal mb-5">Professional Qualifications & Experience</h3>
              <ul className="divide-y divide-ochre/25 border-y border-ochre/25">
                {[
                  ["Masters of Narrative Therapy and Community Work", "University of Melbourne"],
                  ["Diploma of Narrative Therapy and Community Work", ""],
                  ["University Certificate in Trauma", ""],
                  ["Certificate IV in Child, Youth and Family Intervention", ""],
                  ["Certificate IV in Youth Work", ""],
                  ["Certificate III in Fitness", ""],
                  ["Certificate III in Government and Business", ""],
                  ["Certificate in Treating Trauma Using Dialectical Behaviour Therapy (DBT)", ""],
                  ["Suicide First Aid", ""],
                ].map(([t, d]) => (
                  <li key={t} className="py-3.5 flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-charcoal text-[15px]">{t}</span>
                    {d && <span className="text-sm text-earth/70">— {d}</span>}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm leading-relaxed text-earth/80">
                Angel brings 7+ years of professional experience across child protection, child, youth, family,
                social work-aligned and community practice — alongside lived experience as an informal kinship
                carer and formal foster carer, and experience and knowledge as a mother and Aunty.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    {/* WHY I CREATED NGAWILYI */}
    <section className="px-6 py-20 sm:py-28 bg-blush/40 grain relative overflow-hidden">
      <div className="absolute -bottom-28 -left-24 w-96 h-96 bg-terracotta/12 organic-blob-alt blur-3xl" />
      <div className="relative max-w-5xl mx-auto">
        <Reveal>
          <Kicker>Why I Created Ngawilyi Narrative</Kicker>
          <blockquote className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-charcoal">
            "Ngawilyi Narrative grew from seeing the same gap from every side —{" "}
            <span className="italic text-terracotta">as a professional, a carer, a mother and an Aunty.</span>"
          </blockquote>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 text-base lg:text-lg leading-relaxed text-earth">
            Through more than seven years working alongside children, young people and families — and through my
            own experiences caring for children — I kept seeing a space between the different systems surrounding
            families.
          </p>
          <div className="mt-8 rounded-[40px] bg-cream border border-ochre/30 p-10 sm:p-12">
            <div className="flex flex-wrap gap-x-8 gap-y-3 font-display text-2xl sm:text-3xl font-light text-charcoal">
              {["There were therapists.", "There were child protection workers.", "There were schools.", "There were carers and families.", "There were community services."].map((s) => (
                <p key={s}>{s}</p>
              ))}
            </div>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-earth">
              But there were times when what was missing was{" "}
              <em className="font-display text-xl text-terracotta">an Aunty who could move between those spaces.</em>
            </p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {[
              "Someone who could have the therapeutic yarn, but also pick a child up when school had fallen apart.",
              "Someone who could understand behaviour through trauma, story and context, while helping a child reconnect with their strengths.",
              "Someone who could work alongside a carer experiencing system overwhelm while continuing to centre the child's relationships, family, culture and community.",
              "Someone who could provide therapeutic support without expecting therapy to always happen sitting across a desk.",
            ].map((s, i) => (
              <div key={i} className="rounded-3xl bg-cream/70 border border-blush px-7 py-6 text-earth leading-relaxed text-[15px]">{s}</div>
            ))}
          </div>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth">
            My professional experience and study in Narrative Therapy and Community Work — alongside my lived
            experience as a mother, kinship carer, foster carer and Aunty — made me want to build the bridge I
            could see was missing.
          </p>
          <p className="mt-5 font-display italic text-2xl sm:text-3xl text-terracotta">That became Ngawilyi Narrative.</p>
        </Reveal>
      </div>
    </section>

    {/* BRIDGING THERAPY & CULTURE */}
    <section className="px-6 py-20 sm:py-28 bg-charcoal text-cream grain relative overflow-hidden">
      <div className="absolute -top-24 right-1/4 w-96 h-96 bg-terracotta/25 organic-blob blur-3xl" />
      <div className="relative max-w-5xl mx-auto">
        <Reveal>
          <Kicker light>Bridging Therapy & Culture</Kicker>
          <p className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-snug">
            {["Narrative Therapy", "Culture", "Mentoring", "Movement", "Community", "Relationship"].map((w, i, arr) => (
              <span key={w}>
                <span className={i % 2 ? "italic text-ochre" : ""}>{w}</span>
                {i < arr.length - 1 && <span className="text-terracotta"> + </span>}
              </span>
            ))}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 text-base lg:text-lg leading-relaxed text-cream/85">
            Our Aunties are narrative therapists, able to bring professional therapeutic knowledge into culturally
            and relationally grounded spaces. A therapeutic conversation might happen during a traditional therapy
            session — but it might also happen during a walk, fitness session, art activity, community activity,
            school support or yarn.
          </p>
          <p className="mt-6 text-base lg:text-lg leading-relaxed text-cream/85">
            We recognise that healing, identity and wellbeing do not exist separately from a child's relationships,
            culture, family, community and everyday experiences. Ngawilyi Narrative therefore works across those
            spaces — rather than expecting children and families to continually move between disconnected systems.
          </p>
        </Reveal>
      </div>
    </section>

    {/* ALL-ROUND APPROACH */}
    <section className="px-6 py-20 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-12">
          <Kicker>The Aunties All-Round Approach</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">
            One relationship. <span className="italic text-terracotta">Support that responds to the week.</span>
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            "Sometimes a child needs therapy.",
            "Sometimes they need mentoring.",
            "Sometimes they need movement and somewhere safe to regulate.",
            "Sometimes they need support reconnecting with education or community.",
            "Sometimes a family or kinship carer needs someone to step alongside them when systems become overwhelming.",
            "And sometimes they need all of these things at different points in the same week.",
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className={`rounded-3xl px-7 py-6 h-full font-display text-xl sm:text-2xl font-light leading-snug ${i === 5 ? "bg-terracotta text-cream" : "bg-creamdark border border-ochre/25 text-earth"}`}>
                {s}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.35}>
          <p className="mt-10 text-base lg:text-lg leading-relaxed text-earth max-w-3xl">
            The All-Round Approach allows the relationship to remain consistent while the support responds flexibly
            around the child and family — therapy, mentoring and cultural needs woven together by one trusted Aunty.
          </p>
          <p className="mt-6 font-display font-light text-3xl sm:text-4xl text-charcoal">
            We walk alongside — <span className="italic text-terracotta">we don't take over.</span>
          </p>
        </Reveal>
      </div>
    </section>

    {/* MEET OUR AUNTIES */}
    <section className="px-6 py-20 sm:py-28 bg-creamdark grain relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-ochre/20 organic-blob blur-3xl" />
      <div className="relative max-w-7xl mx-auto">
        <Reveal className="mb-12">
          <Kicker>Meet Our Growing Aunty Team</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">
            More Aunties <span className="italic text-terracotta">coming.</span>
          </h2>
          <p className="mt-6 text-base lg:text-lg leading-relaxed text-earth max-w-3xl">
            Ngawilyi Narrative is growing carefully and intentionally. As our programs and community partnerships
            grow, you'll be able to meet more of the Aunties walking alongside our children, young people and
            families here.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div data-testid={`aunty-placeholder-card-${i}`} className="rounded-[36px] bg-cream border border-ochre/30 p-8 h-full">
                <div className="organic-blob aspect-square bg-gradient-to-br from-blush via-ochre/50 to-sunset/60 grain relative flex items-center justify-center">
                  <p className="font-display italic text-earth/70 text-lg text-center px-6">Photo</p>
                </div>
                <h3 className="mt-6 font-display text-2xl text-charcoal">More Aunties Coming</h3>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mt-1">Aunty / Role — to be announced</p>
                <div className="mt-6 space-y-2.5">
                  {[
                    "Name",
                    "Mob / cultural connections — where the individual chooses and has authority to share",
                    "Qualifications",
                    "Therapeutic training",
                    "Professional experience",
                    "Lived and community knowledge",
                    "Areas of practice",
                    "About Me",
                    "What I Bring as an Aunty",
                  ].map((f) => (
                    <div key={f} className="border-b border-dashed border-ochre/40 pb-2">
                      <p className="text-xs text-earth/55">{f}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6"><Tag tone="ochre">Placeholder Profile</Tag></div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="px-6 py-16 bg-cream">
      <Reveal>
        <p className="max-w-5xl mx-auto font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-snug text-charcoal text-left">
          Professional therapeutic practice. Cultural connection.{" "}
          <span className="italic text-terracotta">The relationship of an Aunty.</span>
        </p>
      </Reveal>
    </section>

    <CTABand
      primary={{ to: "/approach", label: "Explore Our Approach" }}
      secondary={{ to: "/referrals", label: "Make a Referral" }}
    />
  </div>
);

export default About;
